import { useEffect, useState } from 'react';
import { 
  KeyboardArrowUp,
  KeyboardArrowDown,
  MoreVert,
  Edit,
  AttachFile as AttachFileIcon,
  PhotoCamera,
  Delete,
  Close as CloseIcon,
} from '@mui/icons-material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import {
  Box,
  Collapse,
  Grid,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  ListItemButton,
  ToggleButton,
  Typography,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  FormControl,
  Alert,
  Paper,
  Chip,
  Tooltip,
  TextField,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { SUCCESS, WARNING } from '../../@iosper/components/LoadingButton/LoadingAlert';
import { useWidth } from '../../@iosper/utils/UseWidth';
import { generarTokenValidacionOrden, saveDocumentacion, saveDocumentacionTemp } from '../../services/Seo/Orden';
import { getFile } from '../../services/Archivos/Files';
import ValidacionOrdenQRPreview from './ValidacionOrdenQRPreview';
import ValidacionOrdenQRMessageEvent from './ValidacionOrdenQRMessageEvent';
import { useUser } from '../Auth/User';
import { PreviewBox } from '../Common/PreviewBox';
import { PreviewFieldContainer } from '../Common/PreviewFieldContainer';
import { PreviewFileField } from '../Common/PreviewFileField';
import FileUpload from '../Common/FileUpload';
import React from 'react';

const ARCHIVOS_REQUERIDOS_MAX_LENGTH = 4;
const ARCHIVOS_REQUERIDOS_MAX_SIZE = 4;
const CONSULTAS_MEDICAS = 92;
const FORMATO_TAMANIO_DOCUMENTACION = `${ARCHIVOS_REQUERIDOS_MAX_SIZE}MB`;

const agruparYOrdenarDocumentacion = (documentacion = []) => {
  if (!Array.isArray(documentacion)) return [];

  const grupos = documentacion.reduce((acc, doc) => {
    const codigo = doc?.codigoDocumentacion;
    if (!codigo) return acc;

    if (!acc[codigo]) {
      acc[codigo] = [];
    }

    acc[codigo].push(doc);
    return acc;
  }, {});

  return Object.keys(grupos)
    .map((codigo) => ({ codigo, documentos: grupos[codigo] }))
    .sort((a, b) => parseInt(a.codigo) - parseInt(b.codigo));
};

const obtenerExtensionDesdeValor = (valor = '') => {
  if (!valor || typeof valor !== 'string') return '';

  const valorLimpio = valor.split('?')[0].split('#')[0];
  const match = valorLimpio.match(/\.([a-z0-9]+)$/i);

  return match ? `.${match[1].toLowerCase()}` : '';
};

const obtenerPreviewImagen = (valor = '', nombreArchivo = '') => {
  const url = typeof valor === 'string' ? valor.trim() : '';
  const extension = obtenerExtensionDesdeValor(url) || obtenerExtensionDesdeValor(nombreArchivo);

  if (!url || !['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(extension)) {
    return null;
  }

  return url;
};

const DescargarDocumento = ({ label, src, externalUrl }) => {
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (downloadUrl?.contentUrl && downloadUrl?.filename) {
      const link = document.createElement('a');
      link.href = downloadUrl?.contentUrl;
      link.download = downloadUrl?.filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return () => URL.revokeObjectURL(downloadUrl?.downloadUrl);
  }, [downloadUrl]);

  const abrirEnlaceExterno = () => {
    if (!externalUrl || !externalUrl.trim()) return;
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  const descargarArchivo = async () => {
    if (externalUrl && externalUrl.trim()) {
      abrirEnlaceExterno();
      return;
    }

    if (!src) {
      return;
    }

    const { apiResponse, contentDisposition } = await getFile(src);

    try {
      let filename = null;
      const filenameRegex = /filename=(.+)/;
      const filenameMatch = contentDisposition.match(filenameRegex);

      if (filenameMatch) {
        const decodedFilename = decodeURIComponent(filenameMatch[1].replaceAll('"', ''));
        filename = decodedFilename;
      } else {
        filename = 'archivo';
      }

      setDownloadUrl({ contentUrl: URL.createObjectURL(apiResponse), filename });
    } catch (error) {
      console.error('Error al procesar la respuesta de la API:', error);
    }
  };

  return (
    <Link
      underline='hover'
      sx={{ cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}
      onClick={() => {
        if (externalUrl && externalUrl.trim()) {
          abrirEnlaceExterno();
          return;
        }

        if (src) {
          descargarArchivo();
        }
      }}
    >
      {label}
      <AttachFileIcon fontSize='small' />
    </Link>
  );
};

const SolicitudDocumentacionOrden = ({ orden, onDocumentacionCargada, onCancelar }) => {
  const [archivosTemp, setArchivosTemp] = useState([]);
  const [cantidadArchivos, setCantidadArchivos] = useState(0);
  const [tamanioArchivos, setTamanioArchivos] = useState(0);
  const [error, setError] = useState('');
  const [contador, setContador] = useState(1);
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  const bytesToMb = (value) => {
    return value / 1048576;
  };

  const onInputFileChange = (file) => {
    const logInfo = {
      fileName: file?.fileName,
      extension: file?.extension,
      size: file?.dimension,
      hasContentBase64: !!file?.contentBase64,
      contentBase64Length: file?.contentBase64?.length || 0,
    };

    if (archivosTemp.length >= ARCHIVOS_REQUERIDOS_MAX_LENGTH) {
      setError('Llegaste al límite de cantidad de archivos que podes cargar.');
      return;
    }

    // FileUpload ya retorna el archivo procesado
    const sizeMB = bytesToMb(file.dimension);
    const sizeFile = sizeMB + tamanioArchivos;

    if (sizeMB > ARCHIVOS_REQUERIDOS_MAX_SIZE) {
      setError(`El archivo supera el tamaño máximo permitido de ${FORMATO_TAMANIO_DOCUMENTACION}.`);
      return;
    }

    if (sizeFile > ARCHIVOS_REQUERIDOS_MAX_SIZE) {
      const totalActual = Number(sizeFile).toFixed(2);
      setError(
        `Superaste el límite de ${FORMATO_TAMANIO_DOCUMENTACION}. El total actual es ${totalActual}MB y no puede exceder ${ARCHIVOS_REQUERIDOS_MAX_SIZE}MB.`,
      );
      return;
    }

    // Convertir a Base64
    convertToBase64(file);
  };

  const convertToBase64 = (file) => {

    const sizeMB = bytesToMb(file.dimension);

    // Determinar si es imagen o no
    let linkPreview = null;
    if (file.extension && ['.jpg', '.jpeg', '.png'].includes(file.extension.toLowerCase())) {
      linkPreview = `data:image;base64,${file.contentBase64}`;
    } 

    const a = {
      numeroOrden: orden.numero.replace(/\./g, ''),
      fileName: file.fileName,
      linkPreview: linkPreview,
      extension: file.extension,
      size: file.dimension,
      contentBase64: file.contentBase64,
      id: contador,
    };

    let existeArchivo = archivosTemp.find(
      (item) => item.fileName === a.fileName && item.size === a.size,
    );

    if (existeArchivo) {
      setError('El archivo que está intentando subir ya existe.');
      return;
    }

    setContador(contador + 1);
    setArchivosTemp([...archivosTemp, a]);
    setCantidadArchivos(cantidadArchivos + 1);
    setTamanioArchivos(tamanioArchivos + sizeMB);
    setError('');
    
  };

  const onDeleteArchivo = (id) => {
    let archivoEliminado = archivosTemp.filter((i) => i.id === id);
    let newListArchivos = archivosTemp.filter((i) => i.id !== id);

    setCantidadArchivos(cantidadArchivos - 1);
    setTamanioArchivos(tamanioArchivos - bytesToMb(archivoEliminado[0].size));
    setArchivosTemp(newListArchivos);
  };

  const handleContinuar = () => {
    if (archivosTemp.length === 0) {
      setError('Debes cargar al menos un archivo.');
      return;
    }
    onDocumentacionCargada(archivosTemp);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        <strong>Para poder continuar adjuntá el Pedido Medico RP</strong>
      </Typography>

      <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
        Cantidad de archivos: <strong>{cantidadArchivos} / 4</strong>
      </Typography>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
        Tamaño máximo permitido: <strong>{FORMATO_TAMANIO_DOCUMENTACION}</strong>
      </Typography>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        Tamaño de Archivos: <strong>{tamanioArchivos.toFixed(2)}MB / {ARCHIVOS_REQUERIDOS_MAX_SIZE}MB</strong>
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ImageList cols={3} rowHeight={100} gap={8}>
          {archivosTemp?.length > 0 &&
            archivosTemp.map((archivo, idx) => (
              <ImageListItem key={`${archivo.fileName}-${idx}`}>
                {archivo.linkPreview ? (
                  <img src={archivo.linkPreview} alt={archivo.fileName} style={{ height: '100px', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0' }}>
                    <Typography variant='caption'>{archivo.extension}</Typography>
                  </Box>
                )}
                <ImageListItemBar
                  title={archivo.fileName}
                  actionIcon={
                    <IconButton
                      onClick={() => onDeleteArchivo(archivo.id)}
                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                    >
                      <Delete />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
        </ImageList>
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <FileUpload
          accept={allowedTypes.join(',')}
          compressFile
          maxFileSize={ARCHIVOS_REQUERIDOS_MAX_SIZE}
          onChangeUpload={onInputFileChange}
          color='neutral'
          startIcon={<PhotoCamera />}
          size='medium'
          variant='contained'
          aria-label='Subir archivo'
          disabled={cantidadArchivos >= ARCHIVOS_REQUERIDOS_MAX_LENGTH}
        >
          Subir archivo
        </FileUpload>
      </FormControl>

      {error && (
        <Typography variant='body2' color='error' sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant='outlined' onClick={onCancelar}>
          Cancelar
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleContinuar}
          disabled={cantidadArchivos === 0}
        >
          Continuar con Validación
        </Button>
      </Box>
    </Box>
  );
};

const CargarDocumentacionOrden = ({ orden, onDocumentacionCargada, onCancelar }) => {
  const [archivosTemp, setArchivosTemp] = useState([]);
  const [cantidadArchivos, setCantidadArchivos] = useState(0);
  const [tamanioArchivos, setTamanioArchivos] = useState(0);
  const [error, setError] = useState('');
  const [contador, setContador] = useState(1);
  const [mostrarSelectorTipoCarga, setMostrarSelectorTipoCarga] = useState(false);
  const [urlExterna, setUrlExterna] = useState('');
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const CODIGO_PEDIDO_MEDICO = '1'; // Código para Pedido Medico

  // Inicializar con la documentación existente de la orden (excluyendo Pedido Medico)
  useEffect(() => {
    if (orden?.documentacion && orden.documentacion.length > 0) {
      const documentacionExistente = orden.documentacion
        .filter((doc) => doc.codigoDocumentacion !== CODIGO_PEDIDO_MEDICO)
        .map((doc, idx) => {
          const enlaceExterno = doc?.enlaceExterno?.trim() || '';
          const linkPreview = obtenerPreviewImagen(enlaceExterno || doc?.path || '', doc?.documentacion || '');
          const extension = obtenerExtensionDesdeValor(enlaceExterno || doc?.documentacion || doc?.path || '') || '';

          return {
            id: -idx - 1,
            fileName: doc.documentacion,
            linkPreview,
            extension,
            size: 0,
            contentBase64: null,
            isExisting: true,
            isExternalLink: !!enlaceExterno,
            enlaceExterno,
            path: doc.path,
            tipoDocumentacion: doc.documentacion,
            idHistorialDocumentacionOrden: doc.idHistorialDocumentacionOrden,
          };
        });
      setArchivosTemp(documentacionExistente);
      setCantidadArchivos(documentacionExistente.length);
    }
  }, [orden]);

  const bytesToMb = (value) => {
    return value / 1048576;
  };

  const onInputFileChange = (file) => {
    if (archivosTemp.length >= ARCHIVOS_REQUERIDOS_MAX_LENGTH) {
      setError('Llegaste al límite de cantidad de archivos que podes cargar.');
      return;
    }

    const sizeMB = bytesToMb(file.dimension);
    const sizeFile = sizeMB + tamanioArchivos;

    if (sizeMB > ARCHIVOS_REQUERIDOS_MAX_SIZE) {
      setError(`El archivo supera el tamaño máximo permitido de ${FORMATO_TAMANIO_DOCUMENTACION}.`);
      return;
    }

    if (sizeFile > ARCHIVOS_REQUERIDOS_MAX_SIZE) {
      const totalActual = Number(sizeFile).toFixed(2);
      setError(
        `Superaste el límite de ${FORMATO_TAMANIO_DOCUMENTACION}. El total actual es ${totalActual}MB y no puede exceder ${ARCHIVOS_REQUERIDOS_MAX_SIZE}MB.`,
      );
      return;
    }

    let linkPreview = null;
    if (file.extension && ['.jpg', '.jpeg', '.png'].includes(file.extension.toLowerCase())) {
      linkPreview = `data:image;base64,${file.contentBase64}`;
    }

    const a = {
      numeroOrden: orden.numero.replace(/\./g, ''),
      fileName: file.fileName,
      linkPreview: linkPreview,
      extension: file.extension,
      size: file.dimension,
      contentBase64: file.contentBase64,
      id: contador,
      isExisting: false,
    };

    let existeArchivo = archivosTemp.find(
      (item) => item.fileName === a.fileName && item.size === a.size,
    );

    if (existeArchivo) {
      setError('El archivo que está intentando subir ya existe.');
      return;
    }

    setContador(contador + 1);
    setArchivosTemp([...archivosTemp, a]);
    setCantidadArchivos(cantidadArchivos + 1);
    setTamanioArchivos(tamanioArchivos + sizeMB);
    setError('');
    
  };

  const onDeleteArchivo = (id) => {
    let archivoEliminado = archivosTemp.filter((i) => i.id === id);
    let newListArchivos = archivosTemp.filter((i) => i.id !== id);

    if (!archivoEliminado[0].isExisting) {
      setCantidadArchivos(cantidadArchivos - 1);
      setTamanioArchivos(tamanioArchivos - bytesToMb(archivoEliminado[0].size));
    } else {
      setCantidadArchivos(cantidadArchivos - 1);
    }
    setArchivosTemp(newListArchivos);
    setError('');
  };

  const cerrarSelectorTipoCarga = () => {
    setMostrarSelectorTipoCarga(false);
    setUrlExterna('');
    setError('');
  };

  const handleAgregarEnlaceExterno = () => {
    const url = urlExterna.trim();

    if (!url) {
      setError('Debes ingresar una URL válida.');
      return;
    }

    try {
      new URL(url);
    } catch {
      setError('La URL ingresada no es válida.');
      return;
    }

    const nombreArchivo = url.split('/').filter(Boolean).pop() || 'documento-enlace-externo';
    const extension = obtenerExtensionDesdeValor(url) || obtenerExtensionDesdeValor(nombreArchivo) || '.pdf';
    const nuevoArchivo = {
      numeroOrden: orden.numero.replace(/\./g, ''),
      fileName: nombreArchivo,
      linkPreview: obtenerPreviewImagen(url, nombreArchivo),
      extension,
      size: 0,
      contentBase64: null,
      id: contador,
      isExisting: false,
      isExternalLink: true,
      enlaceExterno: url,
    };

    const existeArchivo = archivosTemp.find(
      (item) => item.isExternalLink && item.enlaceExterno === url,
    );

    if (existeArchivo) {
      setError('El enlace que está intentando subir ya existe.');
      return;
    }

    setContador(contador + 1);
    setArchivosTemp([...archivosTemp, nuevoArchivo]);
    setCantidadArchivos(cantidadArchivos + 1);
    setError('');
    setUrlExterna('');
    setMostrarSelectorTipoCarga(false);
  };

  const handleGuardar = () => {
    // Filtrar solo los archivos nuevos (no existentes)
    const archivosNuevos = archivosTemp.filter((archivo) => !archivo.isExisting);
    
    // Obtener IDs de archivos existentes que fueron eliminados
    const archivosEliminados = orden.documentacion
      .filter((doc) => doc.codigoDocumentacion !== CODIGO_PEDIDO_MEDICO) // Solo los que podían ser mostrados
      .filter((doc) => !archivosTemp.some((a) => a.idHistorialDocumentacionOrden === doc.idHistorialDocumentacionOrden)) // Los que ya no están en archivosTemp
      .map((doc) => ({ 
        idHistorialDocumentacionOrden: doc.idHistorialDocumentacionOrden,
        documentacion: doc.documentacion 
      }));

    // Permitir guardar si hay archivos nuevos O si hay archivos eliminados
    if (archivosNuevos.length === 0 && archivosEliminados.length === 0) {
      setError('Debes cargar al menos un archivo nuevo o eliminar alguno existente.');
      return;
    }

    // Crear objeto con archivos nuevos y los eliminados
    const datosGuardar = {
      archivosNuevos: archivosNuevos,
      archivosEliminados: archivosEliminados,
    };

    onDocumentacionCargada(datosGuardar);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        <strong>Adjunta los archivos que deseas guardar</strong>
      </Typography>

      <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
        Cantidad de archivos: <strong>{archivosTemp.length} / 4</strong>
      </Typography>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
        Tamaño máximo permitido: <strong>{FORMATO_TAMANIO_DOCUMENTACION}</strong>
      </Typography>
      <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
        Tamaño de Archivos: <strong>{tamanioArchivos.toFixed(2)}MB / {ARCHIVOS_REQUERIDOS_MAX_SIZE}MB</strong>
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ImageList cols={3} rowHeight={100} gap={8}>
          {archivosTemp?.length > 0 &&
            archivosTemp.map((archivo, idx) => {
              const previewImagen = obtenerPreviewImagen(
                archivo?.linkPreview || archivo?.enlaceExterno || archivo?.path || '',
                archivo?.fileName || '',
              );
              const extensionLabel = archivo?.extension || obtenerExtensionDesdeValor(archivo?.enlaceExterno || archivo?.fileName || '') || 'Archivo';

              return (
                <ImageListItem key={`${archivo.fileName}-${idx}`}>
                  {previewImagen ? (
                    <img src={previewImagen} alt={archivo.fileName} style={{ height: '100px', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0' }}>
                      <Typography variant='caption'>
                        {extensionLabel}
                      </Typography>
                    </Box>
                  )}
                  <ImageListItemBar
                    title={archivo.isExternalLink ? 'Enlace externo' : archivo.fileName}
                    subtitle={archivo.isExisting ? 'Existente' : archivo.isExternalLink ? 'URL' : undefined}
                    actionIcon={
                      <IconButton
                        onClick={() => onDeleteArchivo(archivo.id)}
                        sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                      >
                        <Delete />
                      </IconButton>
                    }
                  />
                </ImageListItem>
              );
            })}
        </ImageList>
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <Button
          variant='contained'
          color='neutral'
          onClick={() => setMostrarSelectorTipoCarga(true)}
          disabled={archivosTemp.length >= ARCHIVOS_REQUERIDOS_MAX_LENGTH}
          sx={{ textTransform: 'none' }}
        >
          Subir archivo
        </Button>
      </FormControl>

      <Dialog open={mostrarSelectorTipoCarga} onClose={cerrarSelectorTipoCarga} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Seleccionar origen de la documentación
          <IconButton aria-label='close' onClick={cerrarSelectorTipoCarga} size='small'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant='body2'>Elegí cómo querés cargar la documentación.</Typography>

            <FileUpload
              accept={allowedTypes.join(',')}
              compressFile
              maxFileSize={ARCHIVOS_REQUERIDOS_MAX_SIZE}
              onChangeUpload={(file) => {
                onInputFileChange(file);
                cerrarSelectorTipoCarga();
              }}
              color='neutral'
              startIcon={<PhotoCamera />}
              size='medium'
              variant='contained'
              aria-label='Subir archivo'
              disabled={archivosTemp.length >= ARCHIVOS_REQUERIDOS_MAX_LENGTH}
            >
              Subir archivo
            </FileUpload>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='subtitle2'>Enlace externo</Typography>
              <TextField
                fullWidth
                label='URL externa'
                value={urlExterna}
                onChange={(event) => setUrlExterna(event.target.value)}
                placeholder='https://www.ejemplo.com/documento.pdf'
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant='outlined' onClick={cerrarSelectorTipoCarga}>Cancelar</Button>
              <Button variant='contained' onClick={handleAgregarEnlaceExterno}>Cargar</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {error && (
        <Typography variant='body2' color='error' sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button variant='outlined' onClick={onCancelar}>
          Cancelar
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleGuardar}
          disabled={
            archivosTemp.filter((a) => !a.isExisting).length === 0 &&
            orden.documentacion
              .filter((doc) => doc.codigoDocumentacion !== CODIGO_PEDIDO_MEDICO)
              .filter((doc) => !archivosTemp.some((a) => a.idHistorialDocumentacionOrden === doc.idHistorialDocumentacionOrden)).length === 0
          }
        >
          Guardar
        </Button>
      </Box>
    </Box>
  );
};

const DataCollapseDrawer = (props) => {
  const { orden, modo = 'validar' } = props;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleValidar = (orden) => {
    props.onValidar(orden);
    handleClose();
  };

  const openAction = Boolean(anchorEl);

  return (
    <Box sx={{ width: '100%' }}>
      <ToggleButton
        aria-label='expand row'
        value='check'
        selected={open}
        onChange={() => {
          setOpen(!open);
        }}
        sx={{ border: 0 }}
        fullWidth={true}
      >
        <Grid
          container
          direction='row'
          justifyContent='flex-start'
          alignItems='center'
          gap={2}
          flexWrap='nowrap'
        >
          {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <List sx={{ py: 0 }}>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary={` ${orden.numero || ''}`}
                />
              </ListItem>
            </List>
            {modo === 'validar' && (
              <Box>
                {orden.presentada ? (
                  <Tooltip title='Fue presentada en papel al cobro y registrada en SLP'>
                    <Chip
                      label='Presentada al cobro'
                      color='info'
                      size='small'
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={orden.idTipoOrden === 5 ? 'Orden Validada electrónicamente' : 'Orden pendiente de ser validada electrónicamente'}>
                    <Chip
                      label={orden.idTipoOrden === 5 ? 'Validada' : 'Sin validar'}
                      color={orden.idTipoOrden === 5 ? 'success' : 'warning'}
                      size='small'
                    />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          <List sx={{ p: 0 }}>
            <ListItem sx={{ justifyContent: 'flex-end', p: 0 }}>
              <ListItemButton
                aria-label='more'
                id='long-button'
                aria-controls={openAction ? 'long-menu' : undefined}
                aria-expanded={openAction ? 'true' : undefined}
                aria-haspopup='true'
                onClick={handleClick}
              >
                <MoreVert />
              </ListItemButton>
              <Menu
                id='long-menu'
                MenuListProps={{
                  'aria-labelledby': 'long-button',
                }}
                anchorEl={anchorEl}
                open={openAction}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: 58 * 4.5,
                    width: '200px',
                  },
                }}
              >
                {modo === 'validar' && (
                  <MenuItem
                    key={'validar'}
                    onClick={() => {
                      handleValidar(orden);
                    }}
                    disabled={orden.idTipoOrden === 5 || orden.presentada}
                  >
                    <GridActionsCellItem
                      sx={{ py: 0 }}
                      icon={<Edit />}
                      title='Validar'
                      label='Validar'
                    />
                    <Typography variant='body2'>Validar</Typography>
                  </MenuItem>
                )}
                {modo === 'adjuntar' && (
                  <MenuItem
                    key={'cargar-doc'}
                    onClick={() => {
                      props.onCargarDocumentacion(orden);
                      handleClose();
                    }}
                    disabled={(orden.idTipoOrden !== 2 && orden.idTipoOrden !== 5) || orden.subtipoPrestacion === CONSULTAS_MEDICAS}
                  >
                    <GridActionsCellItem
                      sx={{ py: 0 }}
                      icon={<AttachFileIcon />}
                      title='Adjuntar Informes'
                      label='Adjuntar Informes'
                    />
                    <Typography variant='body2'>Adjuntar Informes</Typography>
                  </MenuItem>
                )}
              </Menu>
            </ListItem>
          </List>
        </Grid>
      </ToggleButton>

      <Collapse in={open} timeout='auto' unmountOnExit>
        <Box sx={{ margin: 1 }}>
          <List>
            <ListItem sx={{ py: 0 }}>
              <ListItemText primary='Fecha' secondary={orden?.fecha || ''} />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText primary='Afiliado' secondary={`${orden?.afiliado}` || ''} />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText primary='Practicas' secondary={`${orden?.practicas}` || ''} />
            </ListItem>
          </List>

          {orden?.documentacion && orden.documentacion.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <PreviewBox title={<Typography variant='body2'>Documentación Presentada</Typography>}>
                <Grid container spacing={2}>
                  {agruparYOrdenarDocumentacion(orden.documentacion).map((grupo) => {
                    if (grupo?.documentos?.length === 0) {
                      return null;
                    }

                    return (
                      <React.Fragment key={grupo.codigo}>
                        {grupo.documentos.map((doc, idx) => {
                          return (
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              key={doc.idHistorialDocumentacionSolicitudCobertura || `${grupo.codigo}-${idx}`}
                            >
                              <PreviewFieldContainer>
                                <PreviewFileField
                                  label={`${doc.documentacion} - ${idx + 1}`}
                                  src={doc?.path || ''}
                                />
                              </PreviewFieldContainer>
                            </Grid>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </PreviewBox>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const OrdenesValidacionTable = ({ ordenes, modo = 'validar', onOrdenValidada, onDocumentacionCargada }) => {
  const screenWidth = useWidth();
  const { user } = useUser();
  const [orden, setOrden] = useState(null);
  // Dialog Orden QR Preview
  const [openQRPreview, setOpenQRPreview] = useState(false);
  const [qrValue, setQrValue] = useState('');
   // Dialog QR Messages Event Preview
  const [messageQr, setMessageQr] = useState('');
  const [qrMessageEventOpen, setQrMessageEventOpen] = useState(false);
  const [qrMessageEventStatus, setQrMessageEventStatus] = useState();

  const [socket, setSocket] = useState(null);

  // Dialog Solicitud Documentación
  const [showSolicitudDocumentacion, setShowSolicitudDocumentacion] = useState(false);
  const [ordenParaDocumentacion, setOrdenParaDocumentacion] = useState(null);
  
  // Dialog Cargar Documentación (para idTipoOrden = 5)
  const [showCargarDocumentacion, setShowCargarDocumentacion] = useState(false);
  const [ordenParaCargarDoc, setOrdenParaCargarDoc] = useState(null);

  const mapOrdenesToRow = (a, index) => ({
    resume: a,
    id: index,
    numero: a.numeroSolicitud,
    fecha: a.fechaSolicitud,
    afiliado: a.nyaAfiliado + ' (' + a.numeroDocumento + ')',
    nyaAfiliado: a.nyaAfiliado,
    numeroDocumento: a.numeroDocumento,
    practicas: a.practicas,
    documentacion: a.documentacion,
    idAfiliado: a.idAfiliado,
    idPrestador: a.idPrestador,
    nombrePrestador: a.nombrePrestador,
    subtipoPrestacion: a.subtipoPrestacion,
    idTipoOrden: a.idTipoOrden,
    presentada: a.presentada,
  });

  const handleValidar = async (orden) => {
    // Si es una consulta médica, pasar directamente al QR sin solicitar documentación
    if (orden.subtipoPrestacion === CONSULTAS_MEDICAS) {
      procedeConValidacion(orden);
      return;
    }

    // Verificar si la orden tiene documentación
    if (!orden.documentacion || orden.documentacion.length === 0) {
      // Si no tiene documentación, mostrar el componente de solicitud
      setOrdenParaDocumentacion(orden);
      setShowSolicitudDocumentacion(true);
    } else {
      // Si tiene documentación, proceder con la validación normal
      procedeConValidacion(orden);
    }
  };

  const procedeConValidacion = async (orden) => {
    setOrden(orden);
    let data = {
      numeroOrden: orden.numero,
      numeroOrdenSinPuntos: orden.numero.replace(/\./g, ''),
      nombreUsuario: user?.nombre,
      fecha: orden.fecha,
      nyaAfiliado: orden.nyaAfiliado,
      numeroDocumento: orden.numeroDocumento,
      idAfiliado: orden.idAfiliado,
      idPrestador: orden.idPrestador,
      nombrePrestador: orden.nombrePrestador,
    };

    const tokenQr = await generarTokenValidacionOrden(data);
    setQrValue(tokenQr);
    setOpenQRPreview(true);
  };

  const handleDocumentacionCargada = async (documentacion) => {
    // Guardar la documentación antes de proceder con la validación
    setShowSolicitudDocumentacion(false);
    
    try {
        let datos = {
          numeroOrden: ordenParaDocumentacion.numero.replace(/\./g, ''),
          documentacion: documentacion,
        };

        await saveDocumentacionTemp(datos);

      // Después de guardar, proceder con la validación
      procedeConValidacion(ordenParaDocumentacion);
    } catch (error) {
      console.error('Error al guardar la documentación:', error);
    }
  };

  const handleCancelarDocumentacion = () => {
    setShowSolicitudDocumentacion(false);
    setOrdenParaDocumentacion(null);
  };

  const handleCargarDocumentacion = (orden) => {
    setOrdenParaCargarDoc(orden);
    setShowCargarDocumentacion(true);
  };

  const handleDocumentacionCargadaOrden = async (datosDocumentacion) => {
    setShowCargarDocumentacion(false);
    
    try {
      // Separar archivos nuevos y eliminados
      const { archivosNuevos, archivosEliminados } = datosDocumentacion;

      let datos = {
        numeroOrden: ordenParaCargarDoc.numero.replace(/\./g, ''),
        documentacion: archivosNuevos,
        archivosEliminados: archivosEliminados,
      };

      await saveDocumentacion(datos);
      
      setOrdenParaCargarDoc(null);
      
      // Llamar al callback del padre para recargar
      onDocumentacionCargada();
    } catch (error) {
      console.error('Error al guardar la documentación:', error);
    }
  };

  const handleCancelarCargarDocumentacion = () => {
    setShowCargarDocumentacion(false);
    setOrdenParaCargarDoc(null);
  };

  useEffect(() => {
    if (socket != null) {
      //  socket.close();
    }

    if (qrValue !== '') {
      conectarWebSocket(qrValue);
    }
  }, [qrValue]);

  const conectarWebSocket = (qrValue) => {
    setSocket(
      new WebSocket(
        `${process.env.REACT_APP_WEBSOCKET_URL}?tokenQr=${qrValue}&token=${localStorage.getItem(
          'token',
        )}`,
      ),
    );
  };

  useEffect(() => {
      if (socket != null) {
        socket.onmessage = function (e) {
          const mssg = JSON.parse(e.data);
          if (mssg.token === qrValue) {
            // dependiendo el state es la vista que va a mostrar
            switch (mssg.state) {
              case 'ErrorOrden':
              case 'ErrorQrEscaneado':
              case 'OrdenCancelada': {
                setOpenQRPreview(false);
                setQrMessageEventOpen(true);
                setMessageQr(mssg.message);
                setQrMessageEventStatus(WARNING);
                socket.close();
                break;
              }
              case 'OrdenVerificada': {
                //const orden = mssg.data;
                setQrMessageEventOpen(false);
                onOrdenValidada();
                socket.close();
                break;
              }
              case 'QrEscaneado': {
                setOpenQRPreview(false);
                setQrMessageEventOpen(true);
                setQrMessageEventStatus(SUCCESS);
                setMessageQr(mssg.message);
                break;
              }
              default:
                socket.close();
            }
          }
        };
      }
    }, [socket]);

  const handlerCerrarQRPreview = () => {
    setOpenQRPreview(false);
    handlerLimpiar();
  };

  const handlerLimpiar = () => {
    socket.close();
  };

  const handlerCerrarQRMessageEvent = () => {
    setQrMessageEventOpen(false);
    handlerLimpiar();
  };


  const columns = [
    {
      field: 'desplegable',
      headerName: 'Nro Orden',
      minHeight: 'unset',
      minWidth: 'unset',
      renderCell: (a) => (
        <DataCollapseDrawer
          orden={a.row}
          modo={modo}
          onValidar={(e) => {
            handleValidar(e);
          }}
          onCargarDocumentacion={(e) => {
            handleCargarDocumentacion(e);
          }}
        />
      ),
    },
    { field: 'fecha', headerName: 'Fecha Emisión', width: 160 },
    { field: 'numero', headerName: 'Nro Orden', width: 100 },
    { field: 'afiliado', headerName: 'Afiliado', flex: 1 },
    { field: 'practicas', headerName: 'Practicas', flex: 1 },
    {
      field: 'documentacion',
      headerName: 'Documentación',
      flex: 1,
      renderCell: (params) => {
        const documentacionList = agruparYOrdenarDocumentacion(params.row.documentacion || []);
        
        if (!documentacionList || documentacionList.length === 0) {
          return <Typography variant='caption'>Sin documentación</Typography>;
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {documentacionList.map((grupo) => {
              if (grupo?.documentos?.length === 0) {
                return null;
              }

              return (
                <Box key={grupo.codigo}>
                  {grupo.documentos.map((doc, idx) => (
                    <Box key={`${grupo.codigo}-${idx}`} sx={{ fontSize: '0.75rem' }}>
                      <DescargarDocumento
                        label={`${doc.documentacion} - ${idx + 1}`}
                        src={doc?.path || ''}
                        externalUrl={doc?.enlaceExterno?.trim() || ''}
                      />
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    // Columna de Estado - solo visible cuando el modo es 'validar'
    ...(modo === 'validar' ? [{
      field: 'estado',
      headerName: 'Estado',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isPresentada = params.row.presentada;
        const isValidada = params.row.idTipoOrden === 5;
        
        // Si presentada es true, mostrar "Presentada al cobro"
        if (isPresentada) {
          return (
            <Tooltip title='Fue presentada en papel al cobro y registrada en SLP'>
              <Chip
                label='Presentada al cobro'
                color='info'
                size='small'
              />
            </Tooltip>
          );
        }
        
        // Si no, mostrar el estado de validación normal
        return (
          <Tooltip title={isValidada ? 'Orden Validada electrónicamente' : 'Orden pendiente de ser validada electrónicamente'}>
            <Chip
              label={isValidada ? 'Validada' : 'Sin validar'}
              color={isValidada ? 'success' : 'warning'}
              size='small'
            />
          </Tooltip>
        );
      },
    }] : []),
    {
      field: 'actions',
      headerName: 'Acciones',
      type: 'actions',
      width: 100,
      getActions: (params) => {
        const actions = [];
        
        // Mostrar "Validar Orden" solo si el modo es 'validar'
        if (modo === 'validar') {
          actions.push(
            <GridActionsCellItem
              icon={<FactCheckOutlinedIcon />}
              title='Validar'
              label='Validar'
              onClick={() => {
                handleValidar(params.row);
              }}
              disabled={params.row.idTipoOrden === 5 || params.row.presentada}
              showInMenu
            />
          );
        }
        
        // Mostrar "Adjuntar Informes" solo si el modo es 'adjuntar'
                if (modo === 'adjuntar') {
          actions.push(
            <GridActionsCellItem
              icon={<AttachFileIcon />}
              title='Adjuntar Informes'
              label='Adjuntar Informes'
              onClick={() => {
                handleCargarDocumentacion(params.row);
              }}
              disabled={(params.row.idTipoOrden !== 2 && params.row.idTipoOrden !== 5) || params.row.subtipoPrestacion === CONSULTAS_MEDICAS}
              showInMenu
            />
          );
        }
        
        return actions;
      },
    },
  ];

  const showHeader = {};
  if (screenWidth && screenWidth === 'xs') {
    showHeader['columnHeaderHeight'] = 0;
  }

  return (
    <div style={{ height: '60vh', width: '100%' }}>
      <Alert severity='info' sx={{ mb: 2 }}>
        Total de órdenes: <strong>{ordenes.length}</strong>
      </Alert>
      <DataGrid
        sx={{
          border: 0,
          '& .MuiDataGrid-cell': {
            py: '8px',
          },
        }}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 200}
        loading={!ordenes.length}
        rows={ordenes.map((a, index) => mapOrdenesToRow(a, index))}
        columns={columns}
        autoPageSize
        pagination={screenWidth === 'xs' ? null : true}
        disableSelectionOnClick
        columnVisibilityModel={{
          desplegable: screenWidth === 'xs',
          numero: screenWidth !== 'xs',
          afiliado: screenWidth !== 'xs',
          practicas: screenWidth !== 'xs',
          documentacion: screenWidth !== 'xs',
          fecha: screenWidth !== 'xs',
          estado: screenWidth !== 'xs' && modo === 'validar',
          actions: screenWidth !== 'xs',
        }}
        disableColumnMenu
        {...showHeader}
      />
      {/* QR */}
      <ValidacionOrdenQRPreview
        open={openQRPreview}
        setOpen={openQRPreview}
        qrValue={qrValue}
        orden={orden}
        handlerCerrarPreview={handlerCerrarQRPreview}
      ></ValidacionOrdenQRPreview>

      {/* Messages */}
      <ValidacionOrdenQRMessageEvent
        open={qrMessageEventOpen}
        orden={orden}
        message={messageQr}
        status={qrMessageEventStatus}
        onHandleCerrar={handlerCerrarQRMessageEvent}
      ></ValidacionOrdenQRMessageEvent>

      {/* Dialog Solicitud Documentación */}
      <Dialog
        open={showSolicitudDocumentacion}
        onClose={handleCancelarDocumentacion}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Validar Orden</DialogTitle>
        <DialogContent>
          {ordenParaDocumentacion && (
            <SolicitudDocumentacionOrden
              orden={ordenParaDocumentacion}
              onDocumentacionCargada={handleDocumentacionCargada}
              onCancelar={handleCancelarDocumentacion}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Cargar Documentación */}
      <Dialog
        open={showCargarDocumentacion}
        onClose={handleCancelarCargarDocumentacion}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Adjuntar Informes</DialogTitle>
        <DialogContent>
          {ordenParaCargarDoc && (
            <CargarDocumentacionOrden
              orden={ordenParaCargarDoc}
              onDocumentacionCargada={handleDocumentacionCargadaOrden}
              onCancelar={handleCancelarCargarDocumentacion}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdenesValidacionTable;
